import {
  DropdownButton,
  type DropdownMenuItemType,
} from '@omnipresentgroup/design-system';
import { useBillingEntities } from '@omnipresentgroup/domains';
import type { InvoiceReport } from '@omnipresentgroup/types';
import { useMemo, useState } from 'react';

import { useManagerUser } from '../../hooks';
import { WrappedMixpanel } from '../../mixpanelWrapper';
import { BankDetailsModal, DirectDebitModal, KollenoModal } from './components';

enum Modals {
  NONE = 'none',
  BANK_DETAILS = 'bank-details',
  KOLLENO = 'kolleno',
  DIRECT_DEBIT = 'direct-debit',
}

type Props = {
  invoice?: InvoiceReport;
};

export const InvoiceReportPayActions = ({ invoice }: Props) => {
  const { companyId, userId } = useManagerUser();

  const [visibleModal, setVisibleModal] = useState<Modals>(Modals.NONE);

  const mixPanelProperties = useMemo(
    () => ({
      companyId,
      userId,
      invoiceId: invoice?.id,
      memo: invoice?.reference,
    }),
    [companyId, userId, invoice],
  );

  const { data: billingEntities } = useBillingEntities({
    companyId,
    enabled: !!companyId,
    throwOnError: false,
  });

  const billingEntity = useMemo(
    () => billingEntities?.find((entity) => entity.companyId === companyId),
    [billingEntities, companyId],
  );

  const dropDownOptions = useMemo(() => {
    const hasSingleBillingEntity = (billingEntities ?? []).length === 1;

    const showDirectDebit =
      hasSingleBillingEntity && billingEntity?.billingEntitiesInfo.formFlag;

    const options = [
      hasSingleBillingEntity && {
        name: 'Pay online',
        onClick: () => {
          setVisibleModal(Modals.KOLLENO);
          WrappedMixpanel.track('Pay Online CTA Clicked', mixPanelProperties);
        },
      },
      {
        name: 'Omnipresent bank details',
        onClick: () => {
          setVisibleModal(Modals.BANK_DETAILS);
          WrappedMixpanel.track(
            'Show bank details clicked',
            mixPanelProperties,
          );
        },
      },
      showDirectDebit && {
        name: 'Enable Direct Debit',
        onClick: () => {
          setVisibleModal(Modals.DIRECT_DEBIT);
          WrappedMixpanel.track(
            'Direct Debit Modal Clicked',
            mixPanelProperties,
          );
        },
      },
    ].filter(Boolean) as DropdownMenuItemType[];
    return options;
  }, [
    billingEntities,
    billingEntity?.billingEntitiesInfo.formFlag,
    mixPanelProperties,
  ]);

  return (
    <>
      <DropdownButton options={dropDownOptions} withSelectableAction />
      <BankDetailsModal
        open={visibleModal === Modals.BANK_DETAILS}
        closeFn={() => setVisibleModal(Modals.NONE)}
      />
      <KollenoModal
        open={visibleModal === Modals.KOLLENO}
        closeFn={() => setVisibleModal(Modals.NONE)}
        invoice={invoice}
      />
      <DirectDebitModal
        open={visibleModal === Modals.DIRECT_DEBIT}
        closeFn={() => setVisibleModal(Modals.NONE)}
        url={`${billingEntity?.directDebitSetupUrl}`}
        invoice={invoice}
      />
    </>
  );
};
